import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import SEO from '../shared/SEO';
import { Code, Menu, X, Sparkles, ArrowRight, Globe, Palette, Zap, Shield, Rocket, CheckCircle, Star, Mail, Phone, MapPin, Send, Users, Award, Clock } from 'lucide-react';

export default function PublicWebsite() {
  const { setCurrentView } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const services = [
    { icon: Globe, title: 'Website Design', desc: 'Beautiful, responsive websites optimized for conversions.', color: '#34d399' },
    { icon: Palette, title: 'Web Applications', desc: 'Custom full-stack applications with modern technologies.', color: '#22d3ee' },
    { icon: Zap, title: 'E-commerce', desc: 'Feature-rich online stores with seamless checkout.', color: '#a78bfa' },
    { icon: Rocket, title: 'SEO', desc: 'Search optimization to increase your visibility.', color: '#f87171' },
    { icon: Code, title: 'Maintenance', desc: 'Ongoing support and performance optimization.', color: '#60a5fa' },
  ];

  const [liveStats, setLiveStats] = useState(null);
  const [isFetchingStats, setIsFetchingStats] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [activeSection, setActiveSection] = useState('home');
  const [reviews, setReviews] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const fetchingRef = useRef(false);

  const fetchPublicStats = useCallback(async () => {
    if (fetchingRef.current) return;
    try {
      fetchingRef.current = true;
      setIsFetchingStats(true);
      setRefreshMessage('');
      console.debug('[PublicWebsite] fetching /api/public/stats');
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const tryUrls = [`${apiBase}/public/stats?ts=${Date.now()}`, `http://localhost:5000/api/public/stats?ts=${Date.now()}`];
      let lastErr = null;
      for (const url of tryUrls) {
        try {
          console.debug('[PublicWebsite] trying', url);
          const res = await fetch(url, { cache: 'no-store' });
          if (!res.ok) throw new Error(`Status ${res.status}`);
          const text = await res.text();
          if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
            console.warn('[PublicWebsite] HTML returned from', url);
            lastErr = new Error('HTML response');
            continue; // try next
          }
          const data = JSON.parse(text);
          console.debug('[PublicWebsite] stats response', data, 'from', url);
          setLiveStats(data);
          setRefreshMessage('Updated');
          setTimeout(() => setRefreshMessage(''), 2500);
          lastErr = null;
          break;
        } catch (err) {
          console.error('[PublicWebsite] attempt failed for', url, err);
          lastErr = err;
          continue;
        }
      }
      if (lastErr) throw lastErr;
    } catch (e) {
      console.error('[PublicWebsite] failed to fetch public stats', e);
      setRefreshMessage('Failed');
      setTimeout(() => setRefreshMessage(''), 2500);
    } finally {
      fetchingRef.current = false;
      setIsFetchingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchPublicStats();
    const onFocus = () => fetchPublicStats();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchPublicStats]);

  useEffect(() => {
    const loadReviews = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        const res = await fetch(`${apiBase}/public/reviews?limit=18`, { cache: 'no-store' });
        if (!res.ok) return setReviews([]);
        const data = await res.json();
        setReviews(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[PublicWebsite] failed to fetch reviews', err);
        setReviews([]);
      }
    };
    loadReviews();
  }, []);

  const canShowReviews = Boolean(liveStats?.showTestimonials) && reviews.length >= Number(liveStats?.minTestimonialsToShow || 3);

  useEffect(() => {
    if (!canShowReviews || reviews.length <= 1) return undefined;
    const timer = setInterval(() => {
      setReviewIndex((idx) => (idx + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [canShowReviews, reviews.length]);

  // Show only stats with a real count > 20
  const visibleStats = [];
  if (liveStats && typeof liveStats.projects === 'number' && liveStats.projects > 20) visibleStats.push({ value: `${liveStats.projects}`, label: 'Projects', icon: Award });
  if (liveStats && typeof liveStats.clients === 'number' && liveStats.clients > 20) visibleStats.push({ value: `${liveStats.clients}`, label: 'Clients', icon: Users });
  if (liveStats && liveStats.supportDisplay) visibleStats.push({ value: liveStats.supportDisplay, label: 'Support', icon: Clock });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you! We will contact you within 24 hours.');
    setFormData({ name: '', email: '', message: '' });
  };

  const btnPrimary = { padding: '1rem 2rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' };
  const cardStyle = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
  const inputStyle = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };

  return (
    <>
      <SEO 
        title="PageHyve Studios | Web Development & Design" 
        description="Professional web design and development agency. We build custom websites, e-commerce stores, and web applications that drive results for your business."
        keywords="web development, web design, custom websites, e-commerce, React, SEO, web applications"
        location="Cumming, GA & Accra, Ghana"
        isLocalBusiness={true}
      />
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #020617, #0f172a, #020617)' }}>
        {/* Decorative blurs */}
        <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
          <div style={{ position: 'absolute', top: '5rem', left: '5%', width: 'min(24rem, 40vw)', height: 'min(24rem, 40vw)', background: 'rgba(16,185,129,0.1)', borderRadius: '50%', filter: 'blur(64px)' }} />
          <div style={{ position: 'absolute', bottom: '5rem', right: '5%', width: 'min(31rem, 40vw)', height: 'min(31rem, 40vw)', background: 'rgba(6,182,212,0.1)', borderRadius: '50%', filter: 'blur(64px)' }} />
        </div>

        {/* Header */}
        <header style={{ position: 'sticky', top: 0, zIndex: 50, borderBottom: '1px solid rgba(255,255,255,0.05)', backdropFilter: 'blur(24px)', background: 'rgba(2,6,23,0.8)' }}>
          <nav style={{ maxWidth: '80rem', margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img src="/logos/pagehyve-icon-color.svg" alt="PageHyve Studios" style={{ width: '2.5rem', height: '2.5rem' }} />
              <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>PageHyve Studios</span>
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }} className="hide-mobile">
              {[{label: 'Services', id: 'services'}, {label: 'Contact', id: 'contact'}].map(item => <a key={item.id} href={`#${item.id}`} onClick={() => setActiveSection(item.id)} style={{ fontSize: '0.875rem', color: activeSection === item.id ? '#34d399' : '#94a3b8' }}>{item.label}</a>)}
              <button onClick={() => setCurrentView('portal')} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 500, borderRadius: '0.5rem' }}>Client Portal</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="hide-desktop" style={{ padding: '0.5rem', color: '#fff' }}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
          {mobileMenuOpen && (
            <div className="hide-desktop" style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'rgba(15,23,42,0.98)', padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              {[{label: 'Services', id: 'services'}, {label: 'Contact', id: 'contact'}].map(item => <a key={item.id} href={`#${item.id}`} onClick={() => { setActiveSection(item.id); setMobileMenuOpen(false); }} style={{ display: 'block', padding: '0.5rem 0', color: activeSection === item.id ? '#34d399' : '#cbd5e1' }}>{item.label}</a>)}
              <button onClick={() => { setCurrentView('portal'); setMobileMenuOpen(false); }} style={{ marginTop: '1rem', color: '#34d399' }}>Client Portal</button>
            </div>
          )}
        </header>

        <main>
          {/* Hero */}
          <section style={{ position: 'relative', zIndex: 10, maxWidth: '80rem', margin: '0 auto', padding: 'clamp(2rem, 6vw, 5rem) 1.5rem clamp(2.5rem, 8vw, 6rem)' }}>
            <div style={{ maxWidth: '56rem' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '9999px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#34d399', fontSize: '0.875rem', marginBottom: '2rem' }}>
                <Sparkles style={{ width: '1rem', height: '1rem' }} /> Transforming Ideas Into Digital Reality
              </div>
              <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: '1.5rem' }}>
                We Build Websites <span style={{ display: 'block', background: 'linear-gradient(to right, #34d399, #22d3ee, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>That Drive Results</span>
              </h1>
              <p style={{ fontSize: '1.25rem', color: '#94a3b8', maxWidth: '42rem', marginBottom: '2.5rem' }}>
                From landing pages to complex applications, we craft digital experiences that grow your business. <strong style={{ color: '#cbd5e1' }}>Free consultation</strong> for new clients.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <a href="#contact" onClick={() => setActiveSection('contact')} style={{ ...btnPrimary, textDecoration: 'none' }}>Start Your Project <ArrowRight style={{ width: '1rem', height: '1rem' }} /></a>
              </div>
            </div>
            {visibleStats.length > 0 && (
              <div style={{ marginTop: '5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '2rem' }}>
                {visibleStats.map((stat, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <stat.icon style={{ width: '1.5rem', height: '1.5rem', color: '#64748b', margin: '0 auto 0.5rem' }} />
                    <div style={{ fontSize: '2.5rem', fontWeight: 700, background: 'linear-gradient(to right, #34d399, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{stat.value}</div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Services */}
          <section id="services" style={{ position: 'relative', zIndex: 10, background: 'rgba(15,23,42,0.5)', padding: '6rem 0' }}>
            <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1.5rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
                <h2 style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: '1rem' }}>Our Services</h2>
                <p style={{ color: '#94a3b8' }}>Comprehensive web solutions tailored to your needs</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
                {services.map((service, i) => (
                  <article key={i} style={{ ...cardStyle, padding: '2rem' }}>
                    <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '0.75rem', background: `${service.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                      <service.icon style={{ width: '1.75rem', height: '1.75rem', color: service.color }} />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem' }}>{service.title}</h3>
                    <p style={{ color: '#94a3b8' }}>{service.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Why Choose Us */}
          <section style={{ position: 'relative', zIndex: 10, background: 'rgba(15,23,42,0.5)', padding: '6rem 0' }}>
            <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1.5rem' }}>
              <h2 style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: '3rem', textAlign: 'center' }}>
                Why Choose <span style={{ color: '#34d399' }}>PageHyve Studios</span>
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                {[
                  { title: 'Custom Solutions', desc: 'No cookie-cutter approaches. Every project is tailored to your specific business needs and goals. We take time to understand your vision.' },
                  { title: 'Modern Technology', desc: 'We use cutting-edge frameworks and tools (React, Node.js, MongoDB) to build scalable, performant applications that grow with your business.' },
                  { title: 'Fair & Transparent Pricing', desc: 'Project-based pricing that reflects the actual complexity and scope of your needs. We provide detailed quotes upfront with no hidden fees.' },
                  { title: 'Ongoing Support', desc: 'We don\'t disappear after launch. Enjoy continuous maintenance, updates, bug fixes, and optimization to keep your platform running smoothly.' },
                  { title: 'Proven Results', desc: 'Multiple successful projects delivered across industries. We\'ve helped businesses increase leads, improve efficiency, and achieve their digital goals.' },
                  { title: 'True Partnership', desc: 'Regular communication, clear timelines, and honest assessments. Your success is our success, and we\'re committed to your long-term growth.' }
                ].map((item, i) => (
                  <article key={i} style={{ ...cardStyle, padding: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <CheckCircle style={{ width: '1.5rem', height: '1.5rem', color: '#34d399', flexShrink: 0 }} />
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>{item.title}</h3>
                    </div>
                    <p style={{ color: '#94a3b8' }}>{item.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {canShowReviews && (
            <section style={{ position: 'relative', zIndex: 10, padding: '5rem 0' }}>
              <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1.5rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>Client Reviews</h2>
                  <p style={{ color: '#94a3b8' }}>What our clients say after project delivery</p>
                </div>
                <div style={{ ...cardStyle, padding: '2rem', minHeight: '220px', display: 'grid', alignItems: 'center' }}>
                  {(() => {
                    const review = reviews[reviewIndex] || null;
                    if (!review) return <div style={{ color: '#64748b' }}>No reviews yet.</div>;
                    return (
                      <div>
                        <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '0.75rem' }}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} size={16} style={{ color: i < Number(review.rating || 0) ? '#fbbf24' : '#334155', fill: i < Number(review.rating || 0) ? '#fbbf24' : 'none' }} />
                          ))}
                        </div>
                        <p style={{ fontSize: '1.1rem', lineHeight: 1.6, color: '#e2e8f0', marginBottom: '1rem' }}>"{review.reviewText}"</p>
                        <div style={{ color: '#34d399', fontWeight: 600 }}>{review.displayName || 'Client'}</div>
                      </div>
                    );
                  })()}
                </div>
                {reviews.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '0.45rem', marginTop: '1rem' }}>
                    {reviews.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setReviewIndex(idx)}
                        aria-label={`Go to review ${idx + 1}`}
                        style={{
                          width: '0.55rem',
                          height: '0.55rem',
                          borderRadius: '999px',
                          background: idx === reviewIndex ? '#34d399' : '#334155',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Contact */}
          <section id="contact" style={{ position: 'relative', zIndex: 10, background: 'rgba(15,23,42,0.5)', padding: '6rem 0' }}>
            <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <h2 style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: '1rem' }}>{liveStats && liveStats.contactHeading ? liveStats.contactHeading : 'Get In Touch'}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button onClick={() => fetchPublicStats()} style={{ padding: '0.35rem 0.6rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)', color: '#cbd5e1', borderRadius: '0.5rem', fontSize: '0.85rem' }}>{isFetchingStats ? 'Refreshing…' : 'Refresh'}</button>
                      {refreshMessage && <span style={{ color: refreshMessage === 'Updated' ? '#34d399' : '#f87171', fontSize: '0.9rem' }}>{refreshMessage}</span>}
                    </div>
                  </div>
                  <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>{liveStats && liveStats.contactSubtext ? liveStats.contactSubtext : 'Ready to start? Contact us for a free consultation.'}</p>
                  <address style={{ fontStyle: 'normal', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {(() => {
                      const items = [];
                      // Show email only if contactEmail is present (avoid exposing blank or default email)
                      if (liveStats && liveStats.contactEmail) {
                        items.push([Mail, liveStats.contactEmail, `mailto:${liveStats.contactEmail}`, '#34d399']);
                      }
                      // Phone and address can be shown independently
                      if ((liveStats && liveStats.contactPhone) || false) items.push([Phone, (liveStats && liveStats.contactPhone) || null, (liveStats && liveStats.contactPhone) ? `tel:${(liveStats.contactPhone || '').replace(/[^+0-9]/g, '')}` : null, '#22d3ee']);
                      items.push([MapPin, (liveStats && liveStats.contactAddress) || 'San Francisco, CA', null, '#a78bfa']);
                      return items.filter(([_, text]) => text).map(([Icon, text, href, color], i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{ width: '3rem', height: '3rem', borderRadius: '0.75rem', background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon style={{ width: '1.25rem', height: '1.25rem', color }} />
                          </div>
                          {href ? <a href={href} style={{ color: '#fff' }}>{text}</a> : <span>{text}</span>}
                        </div>
                      ));
                    })()}
                  </address>
                </div>
                <div style={{ ...cardStyle, padding: '2rem' }}>
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <label htmlFor="name" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Name <span style={{ color: '#f87171' }}>*</span></label>
                      <input id="name" type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} style={inputStyle} placeholder="Your full name" />
                    </div>
                    <div>
                      <label htmlFor="email" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Email <span style={{ color: '#f87171' }}>*</span></label>
                      <input id="email" type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} style={inputStyle} placeholder="your@email.com" />
                    </div>
                    <div>
                      <label htmlFor="company" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Company / Business Name</label>
                      <input id="company" type="text" value={formData.company || ''} onChange={e => setFormData({ ...formData, company: e.target.value })} style={inputStyle} placeholder="Your company name" />
                    </div>
                    <div>
                      <label htmlFor="phone" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Phone Number</label>
                      <input id="phone" type="tel" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={inputStyle} placeholder="(555) 123-4567" />
                    </div>
                    <div>
                      <label htmlFor="projectType" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>What type of project? <span style={{ color: '#f87171' }}>*</span></label>
                      <select id="projectType" required value={formData.projectType || ''} onChange={e => setFormData({ ...formData, projectType: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', appearance: 'none' }}>
                        <option value="">Select a project type...</option>
                        <option value="website">Website Design</option>
                        <option value="ecommerce">E-commerce Store</option>
                        <option value="app">Web Application</option>
                        <option value="redesign">Website Redesign</option>
                        <option value="seo">SEO & Marketing</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="budget" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Budget Range</label>
                      <select id="budget" value={formData.budget || ''} onChange={e => setFormData({ ...formData, budget: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', appearance: 'none' }}>
                        <option value="">Select budget range...</option>
                        <option value="under5k">Under $5,000</option>
                        <option value="5k15k">$5,000 - $15,000</option>
                        <option value="15k50k">$15,000 - $50,000</option>
                        <option value="over50k">Over $50,000</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="timeline" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>When do you need this completed?</label>
                      <select id="timeline" value={formData.timeline || ''} onChange={e => setFormData({ ...formData, timeline: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', appearance: 'none' }}>
                        <option value="">Select timeline...</option>
                        <option value="asap">ASAP (This month)</option>
                        <option value="1month">1-2 months</option>
                        <option value="3months">3-6 months</option>
                        <option value="flexible">Flexible/No deadline</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Project Details <span style={{ color: '#f87171' }}>*</span></label>
                      <textarea id="message" required rows={4} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} style={{ ...inputStyle, resize: 'none' }} placeholder="Tell us about your project, goals, and timeline..." />
                    </div>
                    {(!formData.name || !formData.email || !formData.message || !formData.projectType) && (
                      <div style={{ padding: '1rem', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: '0.5rem', color: '#fca5a5', fontSize: '0.875rem' }}>
                        Please fill in all required fields marked with <span style={{ color: '#f87171' }}>*</span>
                      </div>
                    )}
                    <button type="submit" disabled={!formData.name || !formData.email || !formData.message || !formData.projectType} style={{ ...btnPrimary, justifyContent: 'center', width: '100%', opacity: !formData.name || !formData.email || !formData.message || !formData.projectType ? 0.5 : 1, cursor: !formData.name || !formData.email || !formData.message || !formData.projectType ? 'not-allowed' : 'pointer' }}><Send style={{ width: '1rem', height: '1rem' }} /> Send Message</button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer style={{ position: 'relative', zIndex: 10, borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(2,6,23,0.5)' }}>
          <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '3rem 1.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img src="/logos/pagehyve-icon-color.svg" alt="PageHyve Studios" style={{ width: '2rem', height: '2rem' }} />
              <span style={{ fontWeight: 600 }}>PageHyve Studios</span>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#64748b' }}>© {new Date().getFullYear()} PageHyve Studios. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
